"use client";

import React, { useEffect, useRef, useState } from 'react';

export default function DynamicScripts() {
  const containerRef = useRef(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Defer dynamic scripts until after first paint / idle time to protect mobile LCP and image bandwidth
    const schedule = typeof window !== 'undefined' && 'requestIdleCallback' in window
      ? window.requestIdleCallback
      : (cb) => setTimeout(cb, 2000);

    const handle = schedule(async () => {
      try {
        const baseApiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || 'https://api.codeqlik.com';
        const response = await fetch(`${baseApiUrl}/api/settings/scripts`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.error('Error fetching dynamic scripts:', error);
      }
    });

    return () => {
      if (typeof window !== 'undefined' && 'cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || !settings) return;

    const { chatbotScript, isChatbotEnabled, voiceAgentScript, isVoiceAgentEnabled } = settings;
    
    let combinedScripts = '';
    if (isChatbotEnabled && chatbotScript) {
      combinedScripts += chatbotScript + '\n';
    }
    if (isVoiceAgentEnabled && voiceAgentScript) {
      combinedScripts += voiceAgentScript + '\n';
    }

    if (!combinedScripts) return;

    try {
      // Create a temporary div to parse the HTML string
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = combinedScripts;
      
      const scripts = Array.from(tempDiv.querySelectorAll('script'));
      
      // Clear container before appending
      containerRef.current.innerHTML = '';
      
      // Helper function to load scripts sequentially
      const loadScript = (index) => {
        if (index >= scripts.length) return; // All scripts loaded
        
        const oldScript = scripts[index];
        const newScript = document.createElement('script');
        
        // Copy all attributes
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        
        // Copy inline content if any
        if (oldScript.innerHTML) {
          newScript.innerHTML = oldScript.innerHTML;
        }
        
        if (newScript.src) {
          // If it's an external script, wait for it to load before proceeding
          newScript.onload = () => loadScript(index + 1);
          newScript.onerror = () => {
            console.error('Failed to load script:', newScript.src);
            loadScript(index + 1); // Continue even if one fails
          };
          containerRef.current.appendChild(newScript);
        } else {
          // If it's an inline script, append it and immediately move to the next
          containerRef.current.appendChild(newScript);
          loadScript(index + 1);
        }
      };

      // Start loading the first script
      loadScript(0);

    } catch (error) {
      console.error('Error injecting dynamic scripts:', error);
    }
    
  }, [settings]);

  return <div ref={containerRef} id="dynamic-scripts-container" style={{ display: 'none' }} suppressHydrationWarning />;
}